import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { 
  FaHome, 
  FaGraduationCap, 
  FaQuestionCircle, 
  FaChartLine, 
  FaArrowLeft, 
  FaCheckCircle, 
  FaLock, 
  FaPlay, 
  FaComments, 
  FaCoins, 
  FaUsers,
  FaFlask 
} from 'react-icons/fa';
import AuthService from '../services/AuthService';
import CourseService from '../services/CourseService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createPulse, createSparkle, createGlow } from '../utils/vfx';
import { formatCourseThumbnail, getCourseTopicIcon } from '../utils/courseDisplay';
import BottomNav from './BottomNav';
import './Course.css';

const Course = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const { state } = useUser();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);

  // Authentication check
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!AuthService.isAuthenticated()) {
          navigate('/login', { replace: true });
          return;
        }

        if (!state.user) {
          navigate('/login', { replace: true });
          return;
        }

        setIsAuthenticated(true);
      } catch (error) {
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, state.user]);

  // Load course data
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Don't make API call if courseId is null, undefined, or 'undefined'
    if (!courseId || courseId === 'undefined' || courseId === 'null') {
      console.warn('Course ID is missing or invalid:', courseId, '- redirecting to learn page');
      navigate('/learn', { replace: true });
      return;
    }

    const loadCourse = async () => {
      try {
        setLoading(true);
        const courseData = await CourseService.getCourse(courseId);
        setCourse(courseData.course); // API returns { course: {...} }
        soundEffects.playPageTransition();
      } catch (error) {
        console.error('Failed to load course:', error);
        toast.error(error.message || 'Failed to load course');
        navigate('/learn', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [isAuthenticated, courseId, navigate]);

  const getProgressColor = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500',
      indigo: 'bg-indigo-500'
    };
    return colors[color] || 'bg-blue-500';
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800',
      indigo: 'bg-indigo-100 text-indigo-800'
    };
    return colors[color] || 'bg-blue-100 text-blue-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Course not found</h2>
          <button
            onClick={() => navigate('/learn')}
            className="btn btn-primary"
          >
            ←
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 content-with-nav">
      {/* Top Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e);
                setTimeout(() => navigate('/learn'), 200);
              }}
              onMouseEnter={() => soundEffects.playHover()}
              className="course-back-button"
            >
              <FaArrowLeft className="course-back-icon" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{course.title}</h1>
              <p className="text-sm text-gray-600">{course.lessons?.length || 0} lessons</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">
                Level {state.progress?.level || 1}
              </div>
              <div className="text-xs text-gray-500">
                {state.progress?.xp || 0} XP
              </div>
            </div>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <FaGraduationCap className="text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Course Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-8 shadow-sm mb-6"
        >
          <div className="flex items-start space-x-6">
            <div className="course-thumbnail-large">
              {getCourseTopicIcon(course) ? (
                <img
                  src={getCourseTopicIcon(course)}
                  alt=""
                  aria-hidden="true"
                  className="course-topic-icon-large"
                />
              ) : (
                formatCourseThumbnail(course.thumbnail)
              )}
            </div>
            <div className="flex-1">
              <h2 className="course-title-large">{course.title}</h2>
              <p className="course-description-large">{course.description}</p>
              
              <div className="course-stats-container">
                <div className="course-stat-item">
                  <div className="course-stat-number">
                    {course.units?.flatMap(unit => unit.lessons).length || 0}
                  </div>
                  <div className="course-stat-label">Lessons</div>
                </div>
                <div className="course-stat-divider"></div>
                <div className="course-stat-item">
                  <div className="course-stat-number course-stat-xp">
                    {course.units?.flatMap(unit => unit.lessons).reduce((sum, lesson) => sum + (lesson.xpValue || 0), 0) || 0}
                  </div>
                  <div className="course-stat-label">Total XP</div>
                </div>
                <div className="course-stat-divider"></div>
                <div className="course-stat-item">
                  <div className="course-stat-number">
                    {course.units?.flatMap(unit => unit.lessons).filter(l => l.isCompleted).length || 0}
                  </div>
                  <div className="course-stat-label">Completed</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Course Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-sm"
        >
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Course Content</h3>
          
          {/* Units (3-tier structure) */}
          {course.units && course.units.length > 0 && (
            <div className="space-y-6">
              {course.units.map((unit, unitIndex) => {
                const unitCompleted = unit.lessons?.every(lesson => lesson.isCompleted) || false;
                return (
                  <div 
                    key={unit.id} 
                    className={`course-unit-card ${unitCompleted ? 'course-unit-completed' : ''}`}
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="course-unit-number">
                        {unitIndex + 1}
                      </div>
                      <div>
                        <h4 className="course-unit-title">{unit.title}</h4>
                        <p className="course-unit-description">{unit.description}</p>
                      </div>
                    </div>
                    
                    <div className="course-lessons-list">
                      {unit.lessons?.map((lesson, lessonIndex) => (
                        <div
                          key={lesson.id}
                          onClick={(e) => {
                            if (!lesson.isLocked) {
                              soundEffects.playClick();
                              createRipple(e);
                              setTimeout(() => {
                                navigate(`/course/${courseId}/lesson/${lesson.id}`);
                              }, 200);
                            } else {
                              soundEffects.playError();
                            }
                          }}
                          onMouseEnter={(e) => {
                            if (!lesson.isLocked) {
                              soundEffects.playHover();
                              createPulse(e.currentTarget, 1.02, 200);
                            }
                          }}
                          className={`course-lesson-item ${
                            lesson.isCompleted
                              ? 'course-lesson-completed'
                              : lesson.isLocked
                              ? 'course-lesson-locked'
                              : 'course-lesson-available'
                          }`}
                        >
                          <div className={`course-lesson-number ${
                            lesson.isCompleted
                              ? 'course-lesson-number-completed'
                              : lesson.isLocked
                              ? 'course-lesson-number-locked'
                              : 'course-lesson-number-available'
                          }`}>
                            {lesson.isCompleted ? (
                              <FaCheckCircle className="course-lesson-checkmark" />
                            ) : (
                              lessonIndex + 1
                            )}
                          </div>
                          
                          <div className="course-lesson-content">
                            <div className="course-lesson-header">
                              <h5 className="course-lesson-title">{lesson.title}</h5>
                              <div className="course-lesson-xp">
                                <FaCoins className="course-lesson-xp-icon" />
                                <span className="course-lesson-xp-text">+{lesson.xpValue || 0} XP</span>
                              </div>
                            </div>
                            <div className="course-lesson-footer">
                              {lesson.isCompleted && (
                                <span className="course-lesson-status-completed">Completed</span>
                              )}
                              {lesson.isLocked && (
                                <span className="course-lesson-status-locked">Locked</span>
                              )}
                              {!lesson.isCompleted && !lesson.isLocked && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    soundEffects.playClick();
                                    createRipple(e, '#10b981');
                                    setTimeout(() => {
                                      navigate(`/course/${courseId}/lesson/${lesson.id}`);
                                    }, 200);
                                  }}
                                  onMouseEnter={(e) => {
                                    soundEffects.playHover();
                                    createGlow(e.currentTarget, '#10b981', 300);
                                  }}
                                  className="course-lesson-start-button"
                                >
                                  <FaPlay className="course-lesson-start-icon" />
                                  <span>Start</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
        </motion.div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Course;

